import React from "react";
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from "@mui/material";

import { RenderHTML } from "components";

import { ErrorMessage } from "../../shared/styled-components";
import { useMultiSelectQuestionStyles } from "./style";
import { ErrorMessages } from "utils/constants";

interface MultiSelectOption {
  optionId: string;
  htmlText: string;
  sequence: number;
  isSelected: boolean | null;
}

interface MultiSelectQuestionProps {
  questionId: string;
  questionText: string;
  options: MultiSelectOption[];
  isMandatory: boolean;
  selectedValues: string[];
  showMandatoryError: boolean;
  onChange: (questionId: string, optionId: string, checked: boolean) => void;
}

export const MultiSelectQuestion: React.FC<MultiSelectQuestionProps> = ({
  questionId,
  questionText,
  options,
  isMandatory,
  selectedValues,
  showMandatoryError,
  onChange,
}) => {
  const classes = useMultiSelectQuestionStyles();

  const handleChange = (optionId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(questionId, optionId, event.target.checked);
  };

  const checkboxGroupId = `multiselect-${questionId}`;
  const errorId = `${checkboxGroupId}-error`;

  return (
    <div className={classes.container}>
      <FormControl
        component="fieldset"
        aria-disabled="false"
        error={showMandatoryError && isMandatory}
      >
        <FormLabel
          component="legend"
          htmlFor={checkboxGroupId}
          className={classes.formLabel}
          aria-label={isMandatory ? `${questionText} ${ErrorMessages.InputRequired}` : questionText}
        >
          <div className={classes.legend}>
            <RenderHTML content={questionText ?? ""} />
            {isMandatory && <span className={classes.requiredAsterisk}>*</span>}
          </div>
        </FormLabel>
        <FormGroup
          row={false}
          id={checkboxGroupId}
          className={showMandatoryError && isMandatory ? classes.checkboxGroupError : undefined}
          aria-describedby={showMandatoryError && isMandatory ? errorId : undefined}
        >
          {options?.map((option) => {
            const checkboxId = `checkbox-${questionId}-${option.optionId}`;
            return (
              <FormControlLabel
                key={option.optionId}
                htmlFor={checkboxId}
                control={
                  <Checkbox
                    id={checkboxId}
                    checked={selectedValues.includes(option.optionId)}
                    onChange={handleChange(option.optionId)}
                    color="primary"
                  />
                }
                label={
                  <div className={classes.optionLabel}>
                    <RenderHTML content={option.htmlText ?? ""} />
                  </div>
                }
              />
            );
          })}
        </FormGroup>
      </FormControl>
      {(isMandatory && selectedValues.length === 0) || (showMandatoryError && isMandatory) ? (
        <ErrorMessage id={errorId}>{`${questionText} ${ErrorMessages.InputRequired}`}</ErrorMessage>
      ) : null}
    </div>
  );
};
